class BradleyTheme {
  constructor() {
    this.dialogTriggers();
    this.conciergeDeepLink();
    this.disclosureGroups();
    this.predictiveSearch();
    this.facets();
    this.hoverImages();
    this.canonicalProductSelectors();
    this.productGalleries();
    this.recommendationScrollers();
    this.productRecommendations();
  }

  dialogTriggers() {
document.addEventListener("click", (event) => {
  const productBack = event.target.closest("[data-product-back]");
  if (productBack) {
    const referrer = document.referrer;
    if (referrer) {
      try {
        const previous = new URL(referrer);
        if (previous.origin === window.location.origin && previous.pathname.startsWith("/collections/")) {
          event.preventDefault();
          window.history.back();
          return;
        }
      } catch (_) {
        // Use the collection fallback link when the referrer is unavailable.
      }
    }
  }
      const openTrigger = event.target.closest("[data-dialog-open]");
      const closeTrigger = event.target.closest("[data-dialog-close]");

      if (openTrigger) {
        const dialog = document.getElementById(openTrigger.dataset.dialogOpen);
        if (dialog instanceof HTMLDialogElement) {
          dialog.showModal();
          dialog.querySelector("input, button, a")?.focus();
        }
      }

      if (closeTrigger) {
        closeTrigger.closest("dialog")?.close();
      }
    });

    document.querySelectorAll("dialog").forEach((dialog) => {
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      });
    });

    const feedback = document.querySelector("[data-inquiry-feedback]");
    const feedbackDialog = feedback?.closest("dialog");
    if (feedbackDialog instanceof HTMLDialogElement) {
      feedbackDialog.showModal();
      feedback.focus();
    }
  }

  conciergeDeepLink() {
    const url = new URL(window.location.href);
    if (url.searchParams.get("concierge") !== "open") return;

    const dialog = document.querySelector('[id^="ConciergeDialog-"]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.showModal();
      dialog.querySelector("input, button, a")?.focus();
    }
    url.searchParams.delete("concierge");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  replaceWithParsedChildren(target, source) {
    const fragment = document.createDocumentFragment();
    source.childNodes.forEach((node) => {
      fragment.append(document.importNode(node, true));
    });
    target.replaceChildren(fragment);
  }

  disclosureGroups() {
    document.querySelectorAll("[data-disclosure-group]").forEach((group) => {
      group.addEventListener("toggle", (event) => {
        const active = event.target;
        if (!active.open) return;
        group.parentElement
          ?.querySelectorAll("[data-disclosure-group][open]")
          .forEach((item) => {
            if (item !== active) item.removeAttribute("open");
          });
      }, true);
    });
  }

  predictiveSearch() {
    document.querySelectorAll("[data-predictive-search]").forEach((container) => {
      const input = container.querySelector('input[type="search"]');
      const results = container.querySelector("[data-predictive-results]");
      let request;

      if (!input || !results) return;

      input.addEventListener("input", () => {
        window.clearTimeout(request);
        request = window.setTimeout(async () => {
          const term = input.value.trim();
          if (term.length < 2) {
            results.replaceChildren();
            return;
          }

          const endpoint = `${container.dataset.predictiveSearchUrl}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          try {
            const response = await fetch(endpoint);
            if (!response.ok) throw new Error("Predictive search request failed");
            const html = new DOMParser().parseFromString(await response.text(), "text/html");
            const source = html.querySelector("#shopify-section-predictive-search");
            if (source) this.replaceWithParsedChildren(results, source);
          } catch {
            const message = document.createElement("p");
            message.className = "search-empty";
            message.textContent = "Search suggestions are unavailable. Press enter to search.";
            results.replaceChildren(message);
          }
        }, 180);
      });
    });
  }

  facets() {
    document.querySelectorAll("[data-facets-form]").forEach((form) => {
      form.querySelectorAll("select").forEach((select) => {
        select.addEventListener("change", () => form.requestSubmit());
      });
    });
  }

  hoverImages() {
    const markReady = (image) => {
      if (!(image instanceof HTMLImageElement)) return;
      if (!image.matches(".product-card__image--secondary, .artist-tile__image--secondary")) return;
      if (!(image.complete && image.naturalWidth > 0)) return;
      image
        .closest(".product-card__media, .artist-tile__media")
        ?.classList.add("is-hover-ready");
    };

    document
      .querySelectorAll(".product-card__image--secondary, .artist-tile__image--secondary")
      .forEach((image) => {
        if (image.complete) markReady(image);
        else image.addEventListener("load", () => markReady(image), { once: true });
      });

    document.addEventListener("load", (event) => markReady(event.target), true);
  }

  canonicalProductSelectors() {
    const root = document.querySelector("[data-canonical-product]");
    const variantStateElement = document.querySelector("[data-product-variant-state]");
    if (!root || !variantStateElement) return;

    let state;
    try {
      state = JSON.parse(variantStateElement.textContent);
    } catch {
      return;
    }

    const variants = Array.isArray(state.variants) ? state.variants : [];
    const optionNames = Array.isArray(state.optionNames) ? state.optionNames : [];
    const colorIndex = optionNames.indexOf("Color");
    const groundIndex = optionNames.findIndex((name) =>
      ["Ground / format", "Ground/format", "Ground", "Material", "Product Options"].includes(name)
    );
    const rollIndex = optionNames.findIndex((name) =>
      ["Roll length", "Roll Length", "Size", "Order Unit"].includes(name)
    );
    const saleUnit = typeof state.saleUnit === "string" && state.saleUnit ? state.saleUnit : "Roll";
    const legacyFabricOption = state.legacyFabricOption === true;
    const colorButtons = [...root.querySelectorAll("[data-product-color]")];
    const legacyFabricColors = [...root.querySelectorAll("[data-legacy-fabric-color]")];
    const groundSelect = root.querySelector("[data-product-ground]");
    const rollSelect = root.querySelector("[data-product-roll]");
    const sku = root.querySelector("[data-product-sku]");
    const skuRow = root.querySelector("[data-product-sku-row]");
    const specificationWidth = root.querySelector("[data-product-spec-width]");
    const specificationRepeat = root.querySelector("[data-product-spec-repeat]");
    const price = root.querySelector("[data-product-price]");
    const comparePrice = root.querySelector("[data-product-compare-price]");
    const coverage = root.querySelector("[data-product-coverage]");
    const formVariantId = root.querySelector("[data-product-variant-id]");
    const addToCart = root.querySelector("[data-product-add-to-cart]");
    const addLabel = root.querySelector("[data-product-add-label]");
    const sampleVariantId = root.querySelector("[data-product-sample-variant-id]");
    const sampleAdd = root.querySelector("[data-product-sample-add]");
    const sampleLabel = root.querySelector("[data-product-sample-label]");
    const sampleForSku = root.querySelector("[data-product-sample-for-sku]");
    const sampleColor = root.querySelector("[data-product-sample-color]");
    const sampleGround = root.querySelector("[data-product-sample-ground]");
    const remoteColorwayGallery = state.remoteColorwayGallery && typeof state.remoteColorwayGallery === "object"
      ? state.remoteColorwayGallery
      : {};
    const gallery = root.querySelector(".product-gallery");
    const thumbnailRail = root.querySelector(".product-gallery__thumbnails");

    Object.entries(remoteColorwayGallery).forEach(([color, imageUrls]) => {
      if (!gallery || !thumbnailRail || !Array.isArray(imageUrls)) return;
      imageUrls.forEach((imageUrl, index) => {
        if (typeof imageUrl !== "string" || !imageUrl) return;
        const mediaKey = `remote-${color}-${index}`;
        const item = document.createElement("div");
        item.className = "product-gallery__item";
        item.dataset.galleryItem = "";
        item.dataset.mediaFilename = mediaKey;
        item.dataset.remoteColor = color;
        item.dataset.remoteOrder = String(index + 1);
        item.hidden = true;

        const image = document.createElement("img");
        image.className = "product-gallery__image";
        image.src = imageUrl;
        image.loading = "lazy";
        image.alt = `${state.productTitle || "Louee Vega product"} — ${color} image ${index + 2}`;
        item.append(image);
        gallery.append(item);

        const thumbnail = document.createElement("button");
        thumbnail.className = "product-gallery__thumbnail";
        thumbnail.type = "button";
        thumbnail.dataset.galleryThumbnail = "";
        thumbnail.dataset.mediaFilename = mediaKey;
        thumbnail.dataset.remoteColor = color;
        thumbnail.dataset.remoteOrder = String(index + 1);
        thumbnail.setAttribute("aria-current", "false");
        thumbnail.hidden = true;

        const thumbnailImage = document.createElement("img");
        thumbnailImage.src = imageUrl;
        thumbnailImage.loading = "lazy";
        thumbnailImage.alt = "";
        thumbnail.append(thumbnailImage);
        thumbnailRail.append(thumbnail);
      });
    });

    const galleryItems = [...root.querySelectorAll("[data-gallery-item]")];
    const galleryThumbnails = [...root.querySelectorAll("[data-gallery-thumbnail]")];
    const galleryTotal = root.querySelector("[data-gallery-total]");
    const galleryCurrent = root.querySelector("[data-gallery-current]");
    const galleryControls = root.querySelector("[data-gallery-count]");
    const colorwayMedia = Array.isArray(state.colorwayMedia) ? state.colorwayMedia : [];
    const variantDetails = state.variantDetails && typeof state.variantDetails === "object"
      ? state.variantDetails
      : {};

    const variantValue = (variant, index) =>
      index >= 0 && Array.isArray(variant.options) ? variant.options[index] : "";
    const sameValue = (left, right) => String(left || "").trim() === String(right || "").trim();
    const matches = (variant, color, ground, roll) =>
      (colorIndex < 0 || !color || sameValue(variantValue(variant, colorIndex), color)) &&
      (groundIndex < 0 || !ground || sameValue(variantValue(variant, groundIndex), ground)) &&
      (rollIndex < 0 || !roll || sameValue(variantValue(variant, rollIndex), roll));

    const queryVariantId = new URLSearchParams(window.location.search).get("variant");
    let selectedVariant =
      variants.find((variant) => String(variant.id) === String(queryVariantId || state.selectedVariantId)) ||
      variants[0];
    if (legacyFabricOption && selectedVariant && !variantValue(selectedVariant, groundIndex).includes("Per Yard")) {
      selectedVariant = variants.find((variant) => variantValue(variant, groundIndex).includes("Per Yard")) || selectedVariant;
    }
    if (!selectedVariant) return;

    const updateGallery = (color) => {
      const media = colorwayMedia.find((entry) => sameValue(entry.color, color));
      const orderedFilenames = Array.isArray(media?.mediaFilenames) ? media.mediaFilenames : [];
      const filenames = new Set(orderedFilenames);
      const filenameOrder = new Map(orderedFilenames.map((filename, index) => [filename, index]));
      const remoteItems = galleryItems.filter((item) => sameValue(item.dataset.remoteColor, color));
      const hasRemoteMedia = remoteItems.length > 0;
      const primaryFilename = media?.primaryMediaFilename || orderedFilenames[0];
      const hasMappedMedia = hasRemoteMedia || galleryItems.some((item) => filenames.has(item.dataset.mediaFilename));
      galleryItems.forEach((item, index) => {
        const isRemote = Boolean(item.dataset.remoteColor);
        const isVisibleRemote = hasRemoteMedia && sameValue(item.dataset.remoteColor, color);
        const isVisibleNative = hasRemoteMedia
          ? item.dataset.mediaFilename === primaryFilename
          : filenames.has(item.dataset.mediaFilename);
        item.hidden = hasMappedMedia && !(isRemote ? isVisibleRemote : isVisibleNative);
        const order = isRemote
          ? Number(item.dataset.remoteOrder || index + 1)
          : (hasRemoteMedia ? 0 : (filenameOrder.get(item.dataset.mediaFilename) ?? index));
        item.dataset.galleryOrder = String(order);
      });
      galleryThumbnails.forEach((thumbnail, index) => {
        const isRemote = Boolean(thumbnail.dataset.remoteColor);
        const isVisibleRemote = hasRemoteMedia && sameValue(thumbnail.dataset.remoteColor, color);
        const isVisibleNative = hasRemoteMedia
          ? thumbnail.dataset.mediaFilename === primaryFilename
          : filenames.has(thumbnail.dataset.mediaFilename);
        thumbnail.hidden = hasMappedMedia && !(isRemote ? isVisibleRemote : isVisibleNative);
        const order = isRemote
          ? Number(thumbnail.dataset.remoteOrder || index + 1)
          : (hasRemoteMedia ? 0 : (filenameOrder.get(thumbnail.dataset.mediaFilename) ?? index));
        thumbnail.dataset.galleryOrder = String(order);
        thumbnail.style.order = String(order);
        if (!thumbnail.hidden) thumbnail.setAttribute("aria-label", `View ${color} image ${order + 1}`);
      });
      const visibleItems = galleryItems
        .filter((item) => !item.hidden)
        .sort((left, right) => Number(left.dataset.galleryOrder) - Number(right.dataset.galleryOrder));
      if (galleryTotal) galleryTotal.textContent = String(visibleItems.length);
      if (galleryCurrent) galleryCurrent.textContent = visibleItems.length ? "1" : "0";
      if (galleryControls) galleryControls.hidden = visibleItems.length < 2;
      root.querySelector(".product-gallery")?.dispatchEvent(new CustomEvent("gallery:change"));
    };

    const updateGroundOptions = (color, roll) => {
      if (!(groundSelect instanceof HTMLSelectElement)) return;
      [...groundSelect.options].forEach((option) => {
        option.disabled = !variants.some((variant) => matches(variant, color, option.value, roll));
      });
    };

    const updateRollOptions = (color, ground) => {
      if (!(rollSelect instanceof HTMLSelectElement)) return;
      [...rollSelect.options].forEach((option) => {
        option.disabled = !variants.some((variant) => matches(variant, color, ground, option.value));
      });
    };

    const applyVariant = (variant) => {
      selectedVariant = variant;
      const color = variantValue(variant, colorIndex);
      const ground = variantValue(variant, groundIndex);
      const roll = variantValue(variant, rollIndex);
      const details = variantDetails[String(variant.id)] || {};
      const legacyGroundName = legacyFabricOption ? ground.replace(" - Per Yard", "") : "";
      const sampleVariant = legacyFabricOption
        ? variants.find((candidate) => {
            const option = variantValue(candidate, groundIndex);
            return option.includes(legacyGroundName) && option.includes("Sample");
          })
        : variants.find((candidate) => matches(candidate, color, ground, "Sample"));
      const sampleDetails = sampleVariant ? variantDetails[String(sampleVariant.id)] || {} : {};

      colorButtons.forEach((button) => {
        const hasColor = variants.some((candidate) => matches(candidate, button.dataset.productColor, "", ""));
        button.disabled = !hasColor;
        button.setAttribute("aria-pressed", String(sameValue(button.dataset.productColor, color)));
      });
      updateGroundOptions(color, roll);
      updateRollOptions(color, ground);
      if (groundSelect instanceof HTMLSelectElement && ground) groundSelect.value = ground;
      if (rollSelect instanceof HTMLSelectElement && roll) rollSelect.value = roll;
      if (sku) sku.textContent = variant.sku || "";
      if (skuRow) skuRow.hidden = !variant.sku;
      if (price && details.price) price.textContent = `${details.price}/${saleUnit}`;
      if (comparePrice) {
        comparePrice.textContent = details.compareAtPrice || "";
        comparePrice.hidden = !details.showCompareAt;
      }
      if (coverage) coverage.textContent = details.coverage ? ` · ${details.coverage} sq ft per roll` : "";
      if (specificationWidth && details.width) specificationWidth.textContent = details.width;
      if (specificationRepeat && details.repeat) specificationRepeat.textContent = details.repeat;
      if (formVariantId) formVariantId.value = variant.id;
      if (addToCart) addToCart.disabled = !details.available;
      if (addLabel) addLabel.textContent = details.available ? `Add ${saleUnit.toLowerCase()}s to cart` : "Unavailable";
      if (sampleVariantId) sampleVariantId.value = sampleVariant?.id || "";
      if (sampleAdd) sampleAdd.disabled = !sampleVariant || !sampleDetails.available;
      if (sampleLabel) sampleLabel.textContent = sampleVariant && sampleDetails.available ? "Request sample" : "Sample unavailable";
      if (sampleForSku) sampleForSku.value = variant.sku || "";
      if (sampleColor) sampleColor.value = color;
      if (sampleGround) sampleGround.value = ground;

      document.querySelectorAll("[data-inquiry-color]").forEach((input) => { input.value = color; });
      document.querySelectorAll("[data-inquiry-color-display]").forEach((input) => { input.value = color; });
      document.querySelectorAll("[data-inquiry-ground]").forEach((input) => { input.value = ground; });
      document.querySelectorAll("[data-inquiry-variant-id]").forEach((input) => { input.value = variant.id; });
      document.querySelectorAll("[data-inquiry-sku]").forEach((input) => { input.value = variant.sku || ""; });

      updateGallery(color);
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    };

    colorButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const color = button.dataset.productColor;
        const ground = groundSelect instanceof HTMLSelectElement
          ? groundSelect.value
          : variantValue(selectedVariant, groundIndex);
        const roll = rollSelect instanceof HTMLSelectElement
          ? rollSelect.value
          : variantValue(selectedVariant, rollIndex);
        const variant = variants.find((candidate) => matches(candidate, color, ground, roll)) ||
          variants.find((candidate) => matches(candidate, color, ground, "")) ||
          variants.find((candidate) => matches(candidate, color, "", ""));
        if (variant) applyVariant(variant);
      });
    });

    legacyFabricColors.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        if (groundSelect instanceof HTMLSelectElement) {
          url.searchParams.set("ground", groundSelect.value.replace(" - Per Yard", ""));
        }
        window.location.assign(url);
      });
    });

    groundSelect?.addEventListener("change", () => {
      const color = variantValue(selectedVariant, colorIndex);
      const roll = rollSelect instanceof HTMLSelectElement
        ? rollSelect.value
        : variantValue(selectedVariant, rollIndex);
      const variant = variants.find((candidate) => matches(candidate, color, groundSelect.value, roll)) ||
        variants.find((candidate) => matches(candidate, color, groundSelect.value, ""));
      if (variant) applyVariant(variant);
    });

    rollSelect?.addEventListener("change", () => {
      const color = variantValue(selectedVariant, colorIndex);
      const ground = groundSelect instanceof HTMLSelectElement
        ? groundSelect.value
        : variantValue(selectedVariant, groundIndex);
      const variant = variants.find((candidate) => matches(candidate, color, ground, rollSelect.value));
      if (variant) applyVariant(variant);
    });

    const requestedGround = new URLSearchParams(window.location.search).get("ground");
    if (legacyFabricOption && requestedGround) {
      selectedVariant = variants.find((variant) => {
        const option = variantValue(variant, groundIndex);
        return option.includes(requestedGround) && option.includes("Per Yard");
      }) || selectedVariant;
    }
    applyVariant(selectedVariant);
  }

  productGalleries() {
    document.querySelectorAll("[data-product-gallery]").forEach((shell) => {
      const gallery = shell.querySelector(".product-gallery");
      const allItems = [...shell.querySelectorAll("[data-gallery-item]")];
      const allThumbnails = [...shell.querySelectorAll("[data-gallery-thumbnail]")];
      const byGalleryOrder = (left, right) =>
        Number(left.dataset.galleryOrder || 0) - Number(right.dataset.galleryOrder || 0);
      const visibleItems = () => allItems.filter((item) => !item.hidden).sort(byGalleryOrder);
      const visibleThumbnails = () => allThumbnails.filter((thumbnail) => !thumbnail.hidden).sort(byGalleryOrder);
      const current = shell.querySelector("[data-gallery-current]");
      const previous = shell.querySelector("[data-gallery-previous]");
      const next = shell.querySelector("[data-gallery-next]");

      if (!gallery || !current || allItems.length < 2) return;

      let currentIndex = 0;
      const update = (index) => {
        const items = visibleItems();
        const thumbnails = visibleThumbnails();
        currentIndex = Math.max(0, Math.min(index, items.length - 1));
        allItems.forEach((item) => item.classList.remove("is-active"));
        allThumbnails.forEach((thumbnail) => thumbnail.setAttribute("aria-current", "false"));
        items[currentIndex]?.classList.add("is-active");
        thumbnails[currentIndex]?.setAttribute("aria-current", "true");
        thumbnails[currentIndex]?.scrollIntoView({ block: "nearest", inline: "nearest" });
        current.textContent = String(currentIndex + 1);
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = currentIndex === items.length - 1;
      };
      const goTo = (index) => {
        const items = visibleItems();
        const targetIndex = Math.max(0, Math.min(index, items.length - 1));
        if (!items[targetIndex]) return;
        update(targetIndex);
      };
      allThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", () => {
          const index = visibleThumbnails().indexOf(thumbnail);
          if (index >= 0) goTo(index);
        });
      });
      previous?.addEventListener("click", () => goTo(currentIndex - 1));
      next?.addEventListener("click", () => goTo(currentIndex + 1));
      gallery.addEventListener("gallery:change", () => goTo(0));
      update(0);
    });
  }

  recommendationScrollers(root = document) {
    root.querySelectorAll("[data-recommendations-track]").forEach((track) => {
      if (track.dataset.scrollReady === "true") return;
      const shell = track.closest("product-recommendations");
      const previous = shell?.querySelector("[data-recommendations-previous]");
      const next = shell?.querySelector("[data-recommendations-next]");
      if (!previous || !next) return;

      track.dataset.scrollReady = "true";
      const updateControls = () => {
        previous.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      };
      const scroll = (direction) => {
        const firstCard = track.querySelector(".product-card");
        const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
        const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : track.clientWidth;
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth";
        track.scrollBy({ left: direction * distance, behavior });
      };

      previous.addEventListener("click", () => scroll(-1));
      next.addEventListener("click", () => scroll(1));
      track.addEventListener("scroll", updateControls, { passive: true });
      window.requestAnimationFrame(updateControls);
    });
  }

  productRecommendations() {
    document.querySelectorAll("product-recommendations").forEach(async (element) => {
      if (!element.dataset.url || element.children.length) return;
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), "text/html");
        const source = html.querySelector("product-recommendations");
        if (source?.children.length) {
          this.replaceWithParsedChildren(element, source);
          this.recommendationScrollers(element);
        }
      } catch {
        // Recommendations are optional and should not block the product page.
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => new BradleyTheme());
